import React, { useState, useEffect } from 'react';
import { FiCalendar, FiMapPin, FiUsers } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import PageTitle from '../../components/PageTitle';
import api from '../../services/api';

interface Event {
  id: string;
  title: string;
  description: string;
  featured_image_url: string;
  featured_image_alt: string;
  start_date: string;
  end_date: string;
  location: string;
  price: number;
  capacity: number;
  registered_count: number;
}

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await api.getEvents({ upcoming: true, limit: 50 });
        setEvents(response.events || []);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return 'Date not available';
    }
  };

  const formatPrice = (price: number | undefined) => {
    if (price === undefined || price === 0) return 'Free';
    return `₹${price.toLocaleString('en-IN')}`;
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <PageTitle
        title="Events & Programs"
        subtitle="Stay updated with our latest events, workshops, and community programs."
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-800 rounded-lg overflow-hidden">
                  <div className="aspect-video bg-gray-700" />
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-gray-700 rounded w-3/4" />
                    <div className="h-4 bg-gray-700 rounded w-full" />
                    <div className="h-4 bg-gray-700 rounded w-full" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 bg-gray-800/50 rounded-xl">
            <div className="w-20 h-20 mx-auto mb-6 bg-indigo-500/20 rounded-full flex items-center justify-center">
              <FiCalendar className="w-10 h-10 text-indigo-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">No Upcoming Events</h3>
            <p className="text-gray-400">Check back later for new events.</p>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition duration-300 group"
              >
                <div className="aspect-video bg-gray-700 relative overflow-hidden">
                  {event.featured_image_url ? (
                    <img
                      src={event.featured_image_url}
                      alt={event.featured_image_alt || event.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FiCalendar className="w-12 h-12 text-gray-600" />
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white group-hover:text-indigo-400 transition duration-300">
                    {event.title}
                  </h3>
                  <p className="mt-2 text-gray-400 line-clamp-2">
                    {event.description}
                  </p>

                  <div className="mt-4 space-y-2 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <FiCalendar className="w-4 h-4 text-indigo-400" />
                      <span>{formatDate(event.start_date)}</span>
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-2">
                        <FiMapPin className="w-4 h-4 text-indigo-400" />
                        <span>{event.location}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <FiUsers className="w-4 h-4 text-indigo-400" />
                      <span>{event.registered_count} / {event.capacity} registered</span>
                    </div>
                    <div className="mt-4">
                      <span className={event.price === 0 ? "text-green-400 font-medium" : "text-white font-medium"}>
                        {formatPrice(event.price)}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
